import { useTranslation } from 'react-i18next';
/**
 * `/notifications` — the in-application notification centre.
 *
 * Screen 17. Two panels: what has happened, and what may be emailed about it.
 *
 * The sentence the whole screen rests on:
 *
 * > Notifications tell you what happened. The planning workspace is where it actually happened —
 * > an older message never overrules what you see there.
 *
 * That is not decoration. It is the one place a user is told that an email saying a revision was
 * requested is a copy, and that the workspace is the record (L2-08 §5).
 *
 * **Read state changes nothing.** Marking a Revision Request read does not satisfy it, and
 * dismissing it does not dismiss the obligation — which is why every entry keeps a link back to the
 * place the obligation actually lives (L2-08 §46–47).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type NotificationPreferenceKey, type NotificationPreferences } from '@pius/contracts';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import {
  destinationOf,
  dismiss,
  fetchInbox,
  fetchPreferences,
  markAllRead,
  markRead,
  savePreferences,
  type InboxEntry,
} from './api';
import './notifications.css';

/** What each kind is, in a sentence a person would say. Shared with the History screen. */

const PREFERENCE_KEYS_CONFIG: {
  key: NotificationPreferenceKey;
  labelKey: string;
  noteKey: string;
}[] = [
  {
    key: 'planning_activity',
    labelKey: 'notifications_page.planning_activity_label',
    noteKey: 'notifications_page.planning_activity_note',
  },
  {
    key: 'review_outcomes',
    labelKey: 'notifications_page.review_outcomes_label',
    noteKey: 'notifications_page.review_outcomes_note',
  },
  {
    key: 'lifecycle',
    labelKey: 'notifications_page.lifecycle_label',
    noteKey: 'notifications_page.lifecycle_note',
  },
  {
    key: 'exports',
    labelKey: 'notifications_page.exports_label',
    noteKey: 'notifications_page.exports_note',
  },
];

export function NotificationsScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const inbox = useQuery({ queryKey: ['notifications'], queryFn: fetchInbox });
  const preferences = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchPreferences,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const read = useMutation({ mutationFn: markRead, onSuccess: refresh });
  const readAll = useMutation({ mutationFn: markAllRead, onSuccess: refresh });
  const remove = useMutation({ mutationFn: dismiss, onSuccess: refresh });

  const setPreference = useMutation({
    mutationFn: (next: Partial<NotificationPreferences>) => savePreferences(next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  const error =
    inbox.error instanceof Error
      ? (inbox.error as ApiError)
      : setPreference.error instanceof Error
        ? (setPreference.error as ApiError)
        : undefined;

  const entries = inbox.data?.data ?? [];
  const unread = inbox.data?.unread ?? 0;

  return (
    <div className="ntf">
      <header className="ntf__head">
        <div>
          <h1 className="ntf__title">{t('notifications_page.title')}</h1>
          <p className="ntf__lede">{t('notifications_page.lede')}</p>
        </div>
        <Link className="ntf__link" to="/liturgies">
          {t('nav.liturgies')} →
        </Link>
      </header>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="ntf__grid">
        <section className="ntf__card">
          <div className="ntf__card-head">
            <p className="ntf__eyebrow">
              {t('notifications_page.recent')}{' '}
              {unread > 0 ? (
                <span className="ntf__count">
                  {t('notifications_page.unread_count', { count: unread })}
                </span>
              ) : null}
            </p>
            {unread > 0 ? (
              <button
                type="button"
                className="ntf__ghost"
                onClick={() => readAll.mutate()}
                disabled={readAll.isPending}
              >
                {t('notifications_page.mark_all_read')}
              </button>
            ) : null}
          </div>

          {inbox.isPending ? (
            <p className="ntf__fine">{t('notifications_page.loading')}</p>
          ) : entries.length === 0 ? (
            <p className="ntf__fine">{t('notifications_page.empty')}</p>
          ) : (
            <ul className="ntf__list">
              {entries.map((entry) => (
                <Entry
                  key={entry.id}
                  entry={entry}
                  onRead={() => read.mutate(entry.id)}
                  onDismiss={() => remove.mutate(entry.id)}
                />
              ))}
            </ul>
          )}
        </section>

        <div className="ntf__side">
          <section className="ntf__card">
            <p className="ntf__eyebrow">{t('notifications_page.prefs')}</p>

            {preferences.isPending ? (
              <p className="ntf__fine">{t('notifications_page.loading')}</p>
            ) : (
              <ul className="ntf__prefs">
                {PREFERENCE_KEYS_CONFIG.map(({ key, labelKey, noteKey }) => (
                  <li key={key} className="ntf__pref">
                    <label className="ntf__switch">
                      <input
                        type="checkbox"
                        checked={preferences.data?.data[key] ?? false}
                        onChange={(e) => setPreference.mutate({ [key]: e.target.checked })}
                      />
                      <span className="ntf__pref-label">{t(labelKey)}</span>
                    </label>
                    <p className="ntf__fine">{t(noteKey)}</p>
                  </li>
                ))}
              </ul>
            )}

            <p className="ntf__fine ntf__fine--sep">{t('notifications_page.email_control_note')}</p>
          </section>

          <section className="ntf__card ntf__card--warm">
            <p className="ntf__eyebrow">{t('notifications_page.security')}</p>
            <p className="ntf__body">{t('notifications_page.security_body')}</p>
          </section>
        </div>
      </div>
    </div>
  );
}

function Entry({
  entry,
  onRead,
  onDismiss,
}: {
  entry: InboxEntry;
  onRead: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const destination = destinationOf(entry);
  const itemLabel =
    typeof entry.payload['item_label'] === 'string' ? entry.payload['item_label'] : null;

  return (
    <li className={`ntf__row${entry.read ? '' : ' ntf__row--unread'}`}>
      <div className="ntf__row-main">
        <div className="ntf__row-title">
          {t(`notifications_kinds.${entry.kind}`)}
          {itemLabel ? <span className="ntf__row-item"> · {itemLabel}</span> : null}
        </div>
        <div className="ntf__row-meta">
          {entry.liturgy_title ?? 'Your account'} ·{' '}
          {entry.created_at.slice(0, 16).replace('T', ' ')}
        </div>
      </div>

      <div className="ntf__row-actions">
        {/* Always offered, read or not: the message is a copy, and this is the way back to the
            thing it is a copy of. */}
        {destination ? (
          <Link className="ntf__action" to={destination}>
            {t('notifications_page.open')}
          </Link>
        ) : null}
        {!entry.read ? (
          <button type="button" className="ntf__ghost" onClick={onRead}>
            {t('notifications_page.mark_read')}
          </button>
        ) : null}
        <button type="button" className="ntf__ghost" onClick={onDismiss}>
          {t('notifications_page.dismiss')}
        </button>
      </div>
    </li>
  );
}
